import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { Box } from '@packages/shared/src/components/Box/Box';
import { LazyRender } from '@packages/modules/src/LazyRender/LazyRender';
import dynamic from 'next/dynamic';
import { ErrorBoundary } from '@packages/shared/src/components/ErrorBoundary/ErrorBoundary';
import { registerFlexibleComponentsFooter } from '../../components/ComponentRecognizer/registerFlexibleComponentsFooter';
import type { CmsData } from '../../../interfaces/webapp_json';
import { AreaTop } from '../../components/Footer/AreaTop/AreaTop';
import { AreaSocket } from '../../components/Footer/AreaSocket/AreaSocket';
import { FooterFallbackLoading } from './FooterFallbackLoading';
import { FooterFallbackError } from './FooterFallbackError';

// register the dynamic components
registerFlexibleComponentsFooter();

type PageRendererProps = {
  data: CmsData;
  socketOnly?: boolean;
};

export enum PageRendererTestIds {
  main = 'page-renderer',
  mainMob = 'page-renderer-mob',
}

const DynamicBlockContent = dynamic(
  () =>
    import(
      /* webpackChunkName: 'CMS_FooterBlockContent' */ '../../components/Footer/BlockContent/BlockContent'
    ),
  {
    ssr: false,
  },
);

const DynamicFooterAccordion = dynamic(
  () => import(/* webpackChunkName: 'CMS_FooterAccordion' */ './FooterAccordion'),
  {
    ssr: false,
  },
);

export const PageRenderer = ({ data, socketOnly = false }: PageRendererProps) => {
  const { isMobile } = useDeviceType();

  return (
    <>
      {!socketOnly && <AreaTop data={data} />}
      <Box
        sx={({ spacing }) => ({
          backgroundColor: 'footer.main',
          '& div, & a, & p, & span, & .dangerously-html p a': {
            color: 'footer.contrastText',
          },
          '& a:hover': {
            textDecoration: 'underline',
            color: 'footer.contrastText',
          },
          ...(isMobile
            ? {
                m: spacing(socketOnly ? 11.5 : 6.5, 'auto', 0, 'auto'),
                p: 0,
                typography: 'body1',
              }
            : {
                typography: 'caption',
                p: {
                  margin: 0,
                  '+ p': {
                    pt: 1,
                  },
                },
                '& .footer_block_badges': {
                  display: 'flex',
                  gap: 1.5,
                },
              }),
        })}
        data-testid={isMobile ? PageRendererTestIds.mainMob : PageRendererTestIds.main}
      >
        <ErrorBoundary fallback={<FooterFallbackError />}>
          <LazyRender fallback={<FooterFallbackLoading />}>
            {!socketOnly &&
              (isMobile ? (
                <DynamicFooterAccordion data={data} />
              ) : (
                <DynamicBlockContent data={data} />
              ))}
          </LazyRender>
        </ErrorBoundary>
        <AreaSocket data={data} />
      </Box>
    </>
  );
};
